import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '@/lib/api';
import Seo from '@/components/Seo';
import { applyBrand, resetBrand } from '@/lib/brand';
import { photoUrl } from '@/components/PhotoAttachments';
import { TRADES } from '@/lib/calculators';
import { CheckCircle2, XCircle, AlertTriangle, HardHat, Loader2, Download } from 'lucide-react';

const gbp = (n) => `£${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicQuotePage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, quote: null, business: null });
  const [signature, setSignature] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [responded, setResponded] = useState(null); // 'accepted' | 'declined'
  const [agreedTerms, setAgreedTerms] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/quotes/${token}`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).detail || 'Not found');
        return r.json();
      })
      .then((d) => setState({ loading: false, error: null, ...d }))
      .catch((e) => setState({ loading: false, error: e.message, quote: null, business: null }));
  }, [token]);

  useEffect(() => {
    if (state.business?.brand_color) applyBrand(state.business.brand_color);
    return () => resetBrand();
  }, [state.business]);

  const respond = async (decision) => {
    if (!signature.trim()) {
      alert('Please type your name to confirm.');
      return;
    }
    const hasTerms = !!(state.quote?.terms && state.quote.terms.trim());
    if (decision === 'accepted' && hasTerms && !agreedTerms) {
      alert('Please tick to confirm you agree to the Terms & Conditions before accepting.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/quotes/${token}/respond`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, signature, note, terms_agreed: agreedTerms }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      setResponded(decision);
    } catch (e) {
      alert(e.message);
    } finally { setSubmitting(false); }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4EF]">
        <Loader2 className="animate-spin text-slate-900" size={48}/>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4EF] px-4">
        <div className="tm-card p-10 max-w-md text-center">
          <AlertTriangle className="mx-auto text-brand" size={48}/>
          <h1 className="font-display font-semibold text-3xl uppercase tracking-tight mt-4">Quote not found</h1>
          <p className="text-slate-600 mt-2">This quote link may have expired or been revoked. Please contact the sender for a new one.</p>
        </div>
      </div>
    );
  }
  const { quote, business } = state;
  const accent = business?.brand_color || '#FF5A1F';
  const isOwnerView = !!state.is_owner_view;
  // Customer share link always shows the summary-only view (scope + Subtotal, VAT, Total).
  // Full priced breakdown is available via the downloadable PDF instead.
  const isSimple = true;
  const tradeName = TRADES.find((t) => t.slug === quote.trade)?.name || quote.trade;
  const decided = !!quote.share_decision_at || !!responded;
  const decision = responded || (decided ? quote.status : null);

  // Compute labour costs from raw fields
  const labourCost = (quote.labour_hours || 0) * (quote.labour_rate || 0)
                   + (quote.labour_days || 0) * (quote.labour_day_rate || 0);

  return (
    <div className="min-h-screen bg-[#F4F4EF]">
      <Seo title="Your Quote" noindex />
      <div className="bg-slate-900 text-white border-b-2 border-brand print:hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-semibold tracking-tight">
            <span className="w-7 h-7 rounded-full bg-brand text-brand-ink grid place-items-center">
              <HardHat size={14} strokeWidth={2}/>
            </span>
            <span className="text-lg">TradesMate</span>
            <span className="hidden sm:inline text-xs uppercase tracking-widest text-slate-300 font-semibold">UK</span>
          </div>
          <span className="text-xs uppercase tracking-widest text-slate-300 font-semibold">Secure Quote Link</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex justify-end mb-4 print:hidden">
          <button data-testid="public-quote-print" onClick={() => window.print()} className="tm-btn tm-btn-ghost text-xs min-h-0 py-2">
            <Download size={14}/> Save / print a copy
          </button>
        </div>
        {/* Branded header */}
        <div className="tm-card p-6 sm:p-8 mb-6">
          <div className="h-1.5 -mx-6 sm:-mx-8 -mt-6 sm:-mt-8 mb-6" style={{ background: accent }} data-testid="quote-brand-accent" />
          <header className="flex items-start justify-between border-b-2 pb-4 flex-wrap gap-4" style={{ borderColor: accent }}>
            <div className="flex items-start gap-4">
              {business?.logo_data && (
                <img src={business.logo_data} alt="Logo" className="max-h-20 max-w-[160px] object-contain"/>
              )}
              <div>
                <p className="font-display font-semibold text-xl sm:text-2xl uppercase tracking-tight">
                  {business?.business_name || 'TradesMate User'}
                </p>
                {business?.business_address && (
                  <p className="whitespace-pre-line text-sm text-slate-700 mt-1">{business.business_address}</p>
                )}
                <p className="text-sm text-slate-700 mt-1">
                  {business?.business_phone && <span>{business.business_phone}</span>}
                  {business?.business_phone && business?.business_email && <span> · </span>}
                  {business?.business_email && <span>{business.business_email}</span>}
                </p>
                {business?.business_vat_number && (
                  <p className="text-xs text-slate-600 mt-1">VAT no: {business.business_vat_number}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="font-display font-semibold text-2xl uppercase tracking-tight">QUOTE</p>
              {quote.quote_number && <p className="font-mono text-sm mt-1">No. {quote.quote_number}</p>}
              <p className="font-mono text-xs text-slate-600 mt-1">
                Issued: {quote.created_at ? new Date(quote.created_at).toLocaleDateString('en-GB') : ''}
              </p>
              <p className="font-mono text-xs text-slate-600">{tradeName}</p>
            </div>
          </header>

          <section className="grid sm:grid-cols-2 gap-6 mt-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Quote for</p>
              <p className="font-display font-bold text-lg mt-1">{quote.customer_name || '—'}</p>
              {quote.customer_address && <p className="text-sm whitespace-pre-line mt-1">{quote.customer_address}</p>}
              {quote.customer_phone && <p className="text-sm">{quote.customer_phone}</p>}
              {quote.customer_email && <p className="text-sm">{quote.customer_email}</p>}
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Description</p>
              <p className="font-display font-bold text-lg mt-1">{quote.name}</p>
            </div>
          </section>

          {Array.isArray(quote.items) && quote.items.filter((it) => it.description || it.qty || it.unit_price).length > 0 && (
            <section className="mt-6">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Materials &amp; Line Items</p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-y border-slate-200">
                      <th className="text-left py-2 font-display uppercase text-xs tracking-wider">Description</th>
                      <th className="text-right py-2 w-16 font-display uppercase text-xs tracking-wider">Qty</th>
                      <th className="text-left py-2 w-16 font-display uppercase text-xs tracking-wider">Unit</th>
                      {!isSimple && (
                        <>
                          <th className="text-right py-2 w-24 font-display uppercase text-xs tracking-wider">£/unit</th>
                          <th className="text-right py-2 w-24 font-display uppercase text-xs tracking-wider">Total</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {quote.items.filter((it) => it.description || parseFloat(it.qty) || parseFloat(it.unit_price)).map((it, i) => {
                      const lt = (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0);
                      return (
                        <tr key={i} className="border-b border-slate-300">
                          <td className="py-2">{it.description || '-'}</td>
                          <td className="py-2 text-right font-mono">{it.qty || 0}</td>
                          <td className="py-2 font-mono text-xs">{it.unit || ''}</td>
                          {!isSimple && (
                            <>
                              <td className="py-2 text-right font-mono">{gbp(parseFloat(it.unit_price) || 0)}</td>
                              <td className="py-2 text-right font-mono font-bold">{gbp(lt)}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="grid sm:grid-cols-2 gap-6 mt-6">
            <div>
              {(quote.labour_hours > 0 || quote.labour_days > 0) && (
                <>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Labour</p>
                  <table className="w-full text-sm">
                    <tbody>
                      {isSimple ? (
                        <>
                          {quote.labour_hours > 0 && (
                            <tr><td className="py-1">{quote.labour_hours} {quote.labour_hours === 1 ? 'hour' : 'hours'} of labour included</td></tr>
                          )}
                          {quote.labour_days > 0 && (
                            <tr><td className="py-1">{quote.labour_days} {quote.labour_days === 1 ? 'day' : 'days'} of labour included</td></tr>
                          )}
                        </>
                      ) : (
                        <>
                          {quote.labour_hours > 0 && (
                            <tr><td>{quote.labour_hours} hrs × {gbp(quote.labour_rate)}/hr</td><td className="text-right font-mono">{gbp(quote.labour_hours * quote.labour_rate)}</td></tr>
                          )}
                          {quote.labour_days > 0 && (
                            <tr><td>{quote.labour_days} days × {gbp(quote.labour_day_rate)}/day</td><td className="text-right font-mono">{gbp(quote.labour_days * quote.labour_day_rate)}</td></tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </>
              )}
              {quote.notes && (
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1 font-semibold">Notes</p>
                  <p className="text-sm whitespace-pre-line">{quote.notes}</p>
                </div>
              )}
            </div>
            <div>
              <table className="w-full text-sm">
                <tbody>
                  {isSimple ? (
                    <tr><td className="py-1">Subtotal (excl. VAT)</td><td className="text-right font-mono font-bold">{gbp(quote.subtotal_ex_vat != null ? quote.subtotal_ex_vat : ((quote.subtotal || 0) + (quote.margin_amount || 0)))}</td></tr>
                  ) : (
                    <>
                      <tr><td className="py-1 text-slate-600">Materials</td><td className="text-right font-mono">{gbp(quote.materials_cost)}</td></tr>
                      <tr><td className="py-1 text-slate-600">Labour</td><td className="text-right font-mono">{gbp(labourCost)}</td></tr>
                      <tr className="border-t border-slate-300"><td className="py-1">Subtotal</td><td className="text-right font-mono font-bold">{gbp(quote.subtotal)}</td></tr>
                      <tr><td className="py-1 text-slate-600">Margin ({quote.margin_pct}%)</td><td className="text-right font-mono">{gbp(quote.margin_amount)}</td></tr>
                    </>
                  )}
                  <tr><td className="py-1 text-slate-600">VAT ({quote.vat_rate}%)</td><td className="text-right font-mono">{gbp(quote.vat_amount)}</td></tr>
                  <tr className="border-t border-slate-200"><td className="py-2 font-display font-semibold text-lg uppercase">Total</td><td className="text-right font-mono font-semibold text-xl" style={{ color: accent }}>{gbp(quote.total)}</td></tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {quote.terms && quote.terms.trim() && (
          <div className="tm-card p-6 sm:p-8 mt-6" data-testid="public-quote-terms">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Terms &amp; Conditions</p>
            <p className="text-sm whitespace-pre-line leading-relaxed text-slate-700">{quote.terms}</p>
          </div>
        )}

        {Array.isArray(quote.photos) && quote.photos.filter((p) => p.visible !== false).length > 0 && (
          <div className="tm-card p-6 sm:p-8 mt-6" data-testid="public-quote-photos">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-3 font-semibold">Photos of the work area</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {quote.photos.filter((p) => p.visible !== false).map((p) => (
                <a key={p.id} href={photoUrl(p.storage_path)} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-slate-200 hover:border-brand transition-colors">
                  <img src={photoUrl(p.storage_path)} alt={p.caption || 'Site photo'} className="w-full h-40 object-cover" loading="lazy" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Accept / decline */}
        {isOwnerView ? (
          <div data-testid="owner-preview-banner" className="tm-card p-6 sm:p-8 bg-brand/10 border print:hidden" style={{ borderColor: 'var(--tm-orange)' }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-brand shrink-0 mt-1" size={28}/>
              <div>
                <h2 className="font-display font-semibold text-2xl uppercase tracking-tight">This is your own quote</h2>
                <p className="text-slate-700 mt-2">
                  You&apos;re signed in as the owner of this quote, so the accept / decline form is hidden and your visit doesn&apos;t count as a customer view. Copy the link from your dashboard and share it with your customer instead.
                </p>
              </div>
            </div>
          </div>
        ) : decided ? (
          <div className={`tm-card p-8 text-center ${decision === 'accepted' ? 'bg-emerald-50' : 'bg-rose-50'}`}>
            {decision === 'accepted' ? (
              <CheckCircle2 className="mx-auto text-emerald-600" size={56}/>
            ) : (
              <XCircle className="mx-auto text-rose-600" size={56}/>
            )}
            <h2 className="font-display font-semibold text-3xl uppercase tracking-tight mt-4">
              Quote {decision}
            </h2>
            <p className="text-slate-700 mt-2">
              {decision === 'accepted'
                ? 'Thanks — the trader has been notified and will be in touch to book the job in.'
                : 'The trader has been notified. If this was a mistake, please contact them directly.'}
            </p>
            {quote.share_decision_signature && (
              <p className="text-xs uppercase tracking-widest text-slate-500 mt-3 font-semibold">Signed: {quote.share_decision_signature}</p>
            )}
          </div>
        ) : (
          <div className="tm-card p-6 sm:p-8 print:hidden">
            <h2 className="font-display font-semibold text-2xl uppercase tracking-tight">Your decision</h2>
            <p className="text-slate-600 mt-2">Type your name to confirm, then accept or decline below.</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div>
                <label className="tm-label">Your name (signature)</label>
                <input data-testid="public-signature" value={signature} onChange={(e) => setSignature(e.target.value)} className="tm-input" placeholder="Jane Smith"/>
              </div>
              <div>
                <label className="tm-label">Note (optional)</label>
                <input data-testid="public-note" value={note} onChange={(e) => setNote(e.target.value)} className="tm-input" placeholder="Any conditions or changes…"/>
              </div>
            </div>
            {quote.terms && quote.terms.trim() && (
              <label data-testid="public-terms-agree-label" className="flex items-start gap-3 mt-4 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="public-terms-agree"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="mt-0.5 w-5 h-5 accent-brand shrink-0"
                />
                <span className="text-sm text-slate-700">I have read and agree to the <span className="font-semibold">Terms &amp; Conditions</span> set out above.</span>
              </label>
            )}
            <div className="flex flex-wrap gap-3 mt-5">
              <button
                data-testid="public-accept"
                onClick={() => respond('accepted')}
                disabled={submitting || !signature.trim() || (!!(quote.terms && quote.terms.trim()) && !agreedTerms)}
                className="tm-btn"
              >
                <CheckCircle2 size={18}/> Accept quote
              </button>
              <button
                data-testid="public-decline"
                onClick={() => respond('declined')}
                disabled={submitting || !signature.trim()}
                className="tm-btn tm-btn-ghost"
              >
                <XCircle size={18}/> Decline
              </button>
            </div>
            <p className="font-mono text-xs text-slate-500 mt-4">Quote valid for 30 days from issue. Payment terms: 14 days from invoice unless otherwise agreed.</p>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-500 uppercase tracking-widest print:hidden font-semibold">
          Powered by <a href="https://tradesmateuk.co.uk" target="_blank" rel="noreferrer" className="underline">TradesMate UK</a>
        </p>
      </div>
    </div>
  );
}
